import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { announceCartLinesUpdate, settleWithFetchedCart, announceCartError } from '@theme/standard-cart-events';
import { getLocalStorage, setLocalStorage, removeLocalStorage, isDesktopBreakpoint, moneyFromCents } from '@theme/utilities';
import '@theme/bundle-product-card';

/**
 * A custom element that stages a client-side bundle and bulk-adds it to the cart.
 */
class BundleBuilder extends Component {
  /** @type {Map<string, { id: string, quantity: number, unitPrice: number, title: string, image: string, options: string[], qtyRule: object | undefined }>} */
  #lines = new Map();

  /** @type {object | null} */
  #config = null;

  /** @type {Element | null} */
  #placeholderEl = null;

  /** @type {ResizeObserver | null} */
  #progressResizeObserver = null;

  /** @type {Animation | undefined} The in-flight products slide animation. */
  #productsAnimation;

  /** @type {number} Shop→presentment currency rate, read once (it can't change without a reload). */
  #currencyRate = 1;

  connectedCallback() {
    super.connectedCallback();
    this.#currencyRate = Number(window.Shopify?.currency?.rate) || 1;
    const configEl = this.querySelector('[ref="config"]');
    this.#config = configEl ? JSON.parse(configEl.textContent) : null;
    this.#config?.tiers?.sort((a, b) => this.#threshold(a) - this.#threshold(b));
    this.#placeholderEl = this.querySelector('.bundle-summary__placeholder')?.cloneNode(true) ?? null;
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyChange);
    customElements.upgrade(this);
    this.#restore();
    // #restore already re-renders when it stages lines; only the empty case needs a progress
    // pass to localise the message thresholds into the presentment currency.
    if (this.#lines.size === 0 && this.#currencyRate !== 1 && this.#config?.minimumRequirement === 'purchase_amount') {
      this.#renderProgress();
    }
    this.#syncCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyChange);
    this.#progressResizeObserver?.disconnect();
    this.#progressResizeObserver = null;
    this.#productsAnimation?.cancel();
  }

  /**
   * Handles a quantity-selector update on a summary line.
   *
   * @param {CustomEvent} event - The quantity selector update event.
   */
  #onQtyChange = (event) => {
    const line = event.target.closest('[data-line]');
    if (!line) return;
    event.stopPropagation();
    this.setQty(line.dataset.line, event.detail.quantity);
  };

  get sectionId() {
    return this.dataset.sectionId;
  }

  // ── State ──────────────────────────────────────────────────────────────────

  /**
   * Stages the selected variant of a bundle product card. Bound to on:click="bundle-builder/add".
   *
   * @param {Event} event - The click event from the card's add button.
   */
  add(event) {
    const card = event.target.closest('bundle-product-card');
    const variant = card?.selectedVariant;
    if (!variant || !variant.available) return;
    this.addItem(String(variant.id), this.#lineFromVariant(card, variant, variant.quantity_rule?.min || 1));
  }

  /**
   * Builds a staged line from a card's variant.
   *
   * @param {Element} card - The bundle product card containing the variant.
   * @param {object} variant - The variant data.
   * @param {number} quantity - The line quantity.
   * @returns {object}
   */
  #lineFromVariant(card, variant, quantity) {
    return {
      id: String(variant.id),
      quantity,
      unitPrice: variant.price,
      title: card.querySelector('.product-card__title')?.textContent?.trim() ?? '',
      image: variant.featured_image?.src ?? card.querySelector('.product-card__media img')?.src ?? '',
      options: variant.public_title ? variant.options ?? [] : [],
      qtyRule: variant.quantity_rule,
    };
  }

  /**
   * Adds a staged line for a variant — one variant, one line.
   *
   * @param {string} variantId - The variant id.
   * @param {object} data - The line data.
   */
  addItem(variantId, data) {
    if (this.#lines.has(variantId)) return;
    this.#lines.set(variantId, data);
    this.#hideAtcError();
    this.#save();
    this.render();
    this.#syncCards();
  }

  /**
   * Updates the quantity of a staged line.
   *
   * @param {string} variantId - The variant id.
   * @param {number} qty - The new quantity.
   */
  setQty(variantId, qty) {
    const line = this.#lines.get(variantId);
    if (!line) return;
    if (qty <= 0) return this.removeItem(variantId);
    line.quantity = qty;
    this.#save();
    const list = this.querySelector('[ref="productsAdded"]');
    if (list) this.#renderPlaceholders(list);
    this.#renderSummary();
  }

  /**
   * Removes a staged line.
   *
   * @param {string} variantId - The variant id.
   */
  removeItem(variantId) {
    this.#lines.delete(variantId);
    this.#save();
    this.render();
    this.#syncCards();
  }

  /**
   * Removes the staged line for the clicked row. Bound to on:click="bundle-builder/removeStagedLine".
   *
   * @param {Event} event - The click event from a row's remove button.
   */
  removeStagedLine(event) {
    const line = event.target.closest('[data-line]');
    if (line) this.removeItem(line.dataset.line);
  }

  // ── Cart ────────────────────────────────────────────────────────────────────

  /**
   * Adds all staged lines to the cart, then clears the bundle once the cart drawer has had
   * time to slide in over the summary. Availability errors don't block: Shopify adds what it
   * can, the drawer reflects the real cart and the error notice shows alongside.
   * Bound to on:click="bundle-builder/addBundleToCart".
   */
  async addBundleToCart() {
    if (this.#lines.size === 0) return;
    const btn = this.querySelector('[ref="addBundleButton"]');
    this.#hideAtcError();
    btn?.classList.add('btn--loading');
    if (btn) btn.disabled = true;

    const items = Array.from(this.#lines.values()).map((l) => ({ id: Number(l.id), quantity: l.quantity }));
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const announcement = announceCartLinesUpdate(this, {
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (data.status) {
        // Shopify puts a generic "Cart Error" in message and the actual reason in description.
        const reason = data.description || data.message;
        announcement.reject(new Error(reason));
        announceCartError(this, { error: reason, code: 'INVALID', detail: { description: data.description, errors: data.errors } });
        document.dispatchEvent(new CartErrorEvent(this.sectionId, reason));
        this.#showAtcError(reason);
      }
      document.dispatchEvent(
        new CartAddEvent({}, this.sectionId, {
          source: 'bundle-builder',
          sections: data.sections,
        })
      );

      if (!data.status) {
        settleWithFetchedCart(announcement, { source: 'bundle-builder', sections: data.sections });
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.#clear();
    } catch (error) {
      announcement.reject(error);
      announceCartError(this, { error: error?.message ?? String(error), code: 'SERVICE_UNAVAILABLE' });
      document.dispatchEvent(new CartErrorEvent(this.sectionId, String(error)));
      this.#showAtcError(error.message ?? String(error));
    } finally {
      btn?.classList.remove('btn--loading');
      if (btn) btn.disabled = this.#lines.size === 0 || !this.#requirementMet;
    }
  }

  /**
   * Shows the inline add-to-cart error (mirrors the product form). It stays visible until a
   * new product is staged or the next add-to-cart attempt.
   *
   * @param {string} message - The error message.
   */
  #showAtcError(message) {
    const errorEl = this.querySelector('[ref="addToCartError"]');
    const errorTextEl = this.querySelector('[ref="addToCartTextError"]');
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    const btn = this.querySelector('[ref="addBundleButton"]');
    if (!errorEl || !errorTextEl) return;
    errorEl.classList.remove('hidden');
    errorTextEl.textContent = message;
    if (liveRegion) liveRegion.textContent = message;
    btn?.classList.add('has-error');
  }

  /** Hides the inline add-to-cart error. */
  #hideAtcError() {
    this.querySelector('[ref="addToCartError"]')?.classList.add('hidden');
    this.querySelector('[ref="addBundleButton"]')?.classList.remove('has-error');
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = '';
  }

  /** Resets the bundle to empty. */
  #clear() {
    this.#lines.clear();
    this.#save();
    this.render();
    this.#syncCards();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  /** @type {string} The localStorage key for this section's staged bundle. */
  get #storageKey() {
    return `bundle-builder-${this.sectionId}`;
  }

  /** Persists the staged lines (variant id + quantity) so reloads keep the bundle. */
  #save() {
    const lines = Array.from(this.#lines.values()).map((line) => ({ id: line.id, quantity: line.quantity }));
    if (lines.length === 0) removeLocalStorage(this.#storageKey);
    else setLocalStorage(this.#storageKey, JSON.stringify(lines), 7);
  }

  /** Restores saved lines, re-resolving fresh variant data from the cards on the page. */
  #restore() {
    let saved;
    try {
      saved = JSON.parse(getLocalStorage(this.#storageKey) ?? '[]');
    } catch {
      saved = [];
    }
    if (!Array.isArray(saved) || saved.length === 0) return;

    const cards = Array.from(this.querySelectorAll('bundle-product-card'));
    for (const { id, quantity } of saved) {
      for (const card of cards) {
        const variant = card.getVariant?.(String(id));
        if (!variant?.available || card.requiresSellingPlan) continue;
        const minQty = variant.quantity_rule?.min || 1;
        this.#lines.set(String(variant.id), this.#lineFromVariant(card, variant, Math.max(Number(quantity) || 1, minQty)));
        break;
      }
    }
    if (this.#lines.size > 0) this.render();
  }

  /** Slides the message + products open/closed below lg. Bound to on:click="bundle-builder/toggleProductsAdded". */
  toggleProductsAdded() {
    const body = this.querySelector('[ref="body"]');
    const head = this.querySelector('[ref="head"]');
    if (!body) return;

    if (isDesktopBreakpoint()) return;

    const willOpen = body.hasAttribute('hidden');
    head?.setAttribute('aria-expanded', String(willOpen));

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      body.toggleAttribute('hidden', !willOpen);
      return;
    }

    this.#productsAnimation?.cancel();
    body.removeAttribute('hidden');
    const height = `${body.offsetHeight}px`;
    const gap = `-${parseFloat(getComputedStyle(body.parentElement).rowGap) || 0}px`;
    body.style.overflow = 'hidden';
    this.#productsAnimation = body.animate(
      willOpen
        ? { height: ['0px', height], marginBlockStart: [gap, '0px'] }
        : { height: [height, '0px'], marginBlockStart: ['0px', gap] },
      { duration: 300, easing: 'ease-in-out' }
    );
    this.#productsAnimation.onfinish = () => {
      body.style.removeProperty('height');
      body.style.removeProperty('overflow');
      body.style.removeProperty('margin-block-start');
      body.toggleAttribute('hidden', !willOpen);
    };
  }

  /**
   * Whether a variant is currently staged in the bundle.
   *
   * @param {string} variantId - The variant id.
   * @returns {boolean}
   */
  isStaged(variantId) {
    return this.#lines.has(variantId);
  }

  /** Syncs every card's add button with the current bundle. */
  #syncCards() {
    this.querySelectorAll('bundle-product-card').forEach((card) => card.sync());
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Formats cents using this bundle's configured money format.
   *
   * @param {number} cents - The amount in cents.
   * @param {string} [format] - The money format, defaults to the config's money format.
   * @returns {string}
   */
  #money(cents, format = this.#config?.moneyFormat) {
    return moneyFromCents(cents, format ?? undefined);
  }

  // ── Derived values ──────────────────────────────────────────────────────────

  /** @type {number} The staged lines subtotal, in cents. */
  get subtotal() {
    let cents = 0;
    this.#lines.forEach((l) => (cents += l.unitPrice * l.quantity));
    return cents;
  }

  /** @type {number} The total quantity across the staged lines. */
  get #totalQuantity() {
    return Array.from(this.#lines.values()).reduce((n, l) => n + l.quantity, 0);
  }

  /** @type {number} The value compared against tier thresholds: total quantity, or subtotal in cents. */
  get metric() {
    if (this.#config?.minimumRequirement === 'purchase_amount') return this.subtotal;
    return this.#totalQuantity;
  }

  /**
   * A tier's threshold in the metric's unit, converted to the presentment currency.
   *
   * @param {object} tier - The tier config.
   * @returns {number}
   */
  #threshold(tier) {
    return this.#config?.minimumRequirement === 'purchase_amount'
      ? Math.round(Number(tier.minAmount) * 100 * this.#currencyRate)
      : Number(tier.minQty);
  }

  /** @type {object | null} The highest reached tier. */
  get activeTier() {
    const metric = this.metric;
    let active = null;
    for (const t of this.#config?.tiers ?? []) {
      if (metric >= this.#threshold(t)) active = t;
    }
    return active;
  }

  /** @type {object | null} The first tier not yet reached. */
  get nextTier() {
    const metric = this.metric;
    return (this.#config?.tiers ?? []).find((t) => metric < this.#threshold(t)) ?? null;
  }

  /**
   * @type {number} The estimated saving for the active tier, in cents — never above the
   * subtotal. A fixed amount counts once per order; an amount-off-products discount with
   * discountOncePerOrder off applies per eligible item, mirroring the admin discount setup.
   */
  get saving() {
    const t = this.activeTier;
    if (!t) return 0;
    if (this.#config.discountValueType === 'percentage') {
      return Math.round((this.subtotal * Number(t.percentage)) / 100);
    }
    let saving = Math.round(Number(t.amount) * 100 * this.#currencyRate);
    if (this.#config.discountOncePerOrder === false) saving *= this.#totalQuantity;
    return Math.min(this.subtotal, saving);
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  render() {
    this.#renderItems();
    this.#renderSummary();
  }

  /** Updates totals, progress and the add button — everything but rebuilding the item rows. */
  #renderSummary() {
    this.#renderTotals();
    this.#renderProgress();
    const atc = this.querySelector('[ref="addBundleButton"]');
    if (atc) atc.disabled = this.#lines.size === 0 || !this.#requirementMet;
  }

  /** @type {boolean} Whether the staged bundle meets the first tier's minimum requirement. */
  get #requirementMet() {
    const firstTier = this.#config?.tiers?.[0];
    return !firstTier || this.metric >= this.#threshold(firstTier);
  }

  /** Renders the staged lines and the remaining placeholders. */
  #renderItems() {
    const list = this.querySelector('[ref="productsAdded"]');
    const tpl = this.querySelector('[ref="itemTemplate"]');
    if (!list || !tpl) return;
    list.innerHTML = '';
    this.#lines.forEach((line, id) => {
      const node = tpl.content.firstElementChild.cloneNode(true);
      node.dataset.line = id;
      const thumb = node.querySelector('[data-thumb]');
      if (thumb && line.image) {
        const sized = (width) => {
          const url = new URL(line.image, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        };
        thumb.src = sized(200);
        thumb.srcset = `${sized(200)} 200w, ${sized(400)} 400w`;
        thumb.alt = line.title;
      }
      node.querySelector('[data-title]').textContent = line.title;
      const optionsEl = node.querySelector('[data-variant]');
      if (line.options.length) {
        line.options.forEach((value, index) => {
          if (index > 0) {
            const separator = document.createElement('span');
            separator.className = 'cart-item__option-separator';
            separator.textContent = '|';
            optionsEl.appendChild(separator);
          }
          const option = document.createElement('span');
          option.className = 'cart-item__option-value';
          option.textContent = value;
          optionsEl.appendChild(option);
        });
      } else {
        optionsEl.remove();
      }
      node.querySelector('[data-price]').textContent = this.#money(line.unitPrice);

      const qtyInput = node.querySelector('quantity-selector-component input');
      if (qtyInput) {
        // Resolve the [variant_id] placeholder from the template so the id is unique
        // per line; the id format itself stays defined in Liquid.
        qtyInput.id = qtyInput.id.replace('[variant_id]', id);
        node.querySelector('quantity-selector-component label')?.setAttribute('for', qtyInput.id);
        qtyInput.value = String(line.quantity);
        const rule = line.qtyRule;
        if (rule) {
          qtyInput.min = String(rule.min || 1);
          if (rule.max) qtyInput.max = String(rule.max);
          qtyInput.step = String(rule.increment || 1);
        }
      }

      list.appendChild(node);
    });
    this.#renderPlaceholders(list);
  }

  /**
   * The number of placeholder rows to show for the current metric.
   *
   * @returns {number}
   */
  #placeholderCount() {
    const base = Number(this.#config?.placeholderBase ?? 0);
    const tier = this.#config?.tiers?.[0];
    if (this.#config?.minimumRequirement === 'purchase_amount') {
      if (tier && this.metric >= this.#threshold(tier)) return 0;
      return Math.max(0, base - this.#lines.size);
    }
    return Math.max(0, base - this.metric);
  }

  /**
   * Appends placeholder rows to the items list.
   *
   * @param {Element} list - The items list element.
   */
  #renderPlaceholders(list) {
    list.querySelectorAll('.bundle-summary__placeholder').forEach((el) => el.remove());
    if (!this.#placeholderEl) return;
    const count = this.#placeholderCount();
    for (let i = 0; i < count; i += 1) {
      list.appendChild(this.#placeholderEl.cloneNode(true));
    }
  }

  /** Renders the total and the saving pill. */
  #renderTotals() {
    const saving = this.saving;
    const totalEl = this.querySelector('[ref="total"]');
    const savingEl = this.querySelector('[ref="saving"]');
    if (totalEl) totalEl.textContent = this.#money(Math.max(0, this.subtotal - saving), this.#config?.moneyWithCurrencyFormat);
    if (savingEl) {
      savingEl.hidden = saving <= 0;
      if (saving > 0) {
        savingEl.textContent = (savingEl.dataset.template ?? '').replace('[amount]', this.#money(saving));
      }
    }
  }

  /**
   * Publishes the half-node lead as a share of the fill's travel, so CSS can light a node the
   * moment the bar touches its edge rather than its centre. Re-read whenever the track resizes.
   */
  #syncEdgeSlack() {
    const progressEl = this.querySelector('.bundle-tier-progress');
    const trackEl = progressEl?.querySelector('.bundle-tier-progress__track');
    const nodeEl = progressEl?.querySelector('.bundle-tier-progress__node');
    if (!progressEl || !trackEl || !nodeEl) return;

    this.#progressResizeObserver ??= new ResizeObserver(() => this.#syncEdgeSlack());
    this.#progressResizeObserver.disconnect();
    this.#progressResizeObserver.observe(trackEl);

    const inset = parseFloat(getComputedStyle(progressEl).getPropertyValue('--tier-inset')) || 0;
    const travel = trackEl.getBoundingClientRect().width - inset;
    const slack = travel > 0 ? nodeEl.getBoundingClientRect().width / 2 / travel : 0;
    progressEl.style.setProperty('--edge-slack', String(slack));
  }

  /** Renders the tier progress fill and the progress message. */
  #renderProgress() {
    const messageEl = this.querySelector('[ref="messageText"]');
    const tiers = this.#config?.tiers ?? [];
    const metric = this.metric;
    const nextTier = tiers.find((tier) => metric < this.#threshold(tier)) ?? null;
    const reachedCount = nextTier ? tiers.indexOf(nextTier) : tiers.length;

    let ratio = 0;
    let overshoot = 0;
    if (tiers.length > 0) {
      if (reachedCount >= tiers.length) {
        ratio = 1;
        overshoot = 1;
      } else {
        const previousThreshold = reachedCount > 0 ? this.#threshold(tiers[reachedCount - 1]) : 0;
        const upcomingThreshold = this.#threshold(tiers[reachedCount]);
        const segmentFraction = upcomingThreshold > previousThreshold
          ? (metric - previousThreshold) / (upcomingThreshold - previousThreshold)
          : 0;
        ratio = (reachedCount + Math.min(1, Math.max(0, segmentFraction))) / tiers.length;
      }
    }
    const progressEl = this.querySelector('.bundle-tier-progress');
    if (progressEl) {
      progressEl.style.setProperty('--progress-ratio', String(ratio));
      progressEl.style.setProperty('--progress-overshoot', String(overshoot));
      progressEl.style.setProperty('--reached-count', String(reachedCount));
      this.#syncEdgeSlack();
    }

    if (!messageEl) return;

    if (this.#lines.size > 0 && !nextTier) {
      messageEl.textContent = this.#config?.rewardMessage ?? '';
      return;
    }

    const targetTier = nextTier ?? tiers[0];
    if (!targetTier) {
      messageEl.textContent = '';
      return;
    }
    const remainingToTarget = Math.max(0, this.#threshold(targetTier) - metric);
    const remainingText = this.#config?.minimumRequirement === 'purchase_amount'
      ? this.#money(remainingToTarget)
      : String(remainingToTarget);
    messageEl.innerHTML = (this.#config?.progressMessage ?? '')
      .replaceAll('[x]', `<strong>${remainingText}</strong>`)
      .replaceAll('[label]', `<strong>${targetTier.label ?? ''}</strong>`);
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
